import React, { useState, useCallback, useEffect } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { motion } from 'framer-motion';
import { Star, CheckCircle, Loader2 } from 'lucide-react';
import cntl from 'cntl';
import useReviews from '@/hooks/useReviews';
import { Product } from '@/context/types';
import WriteReviewForm from './WriteReviewForm';

// ============================================================================
// Types
// ============================================================================
export interface Review {
  id: string;
  author: string;
  title: string;
  body: string;
  rating: number;
  date: string;
  images: string[];
  isVerified: boolean; // <-- Add this new field
}

export interface ProductReviewsData {
  averageRating: number;
  totalReviews: number;
  reviews: Review[];
}

interface CustomerReviewsProps {
  initialReviews: Review[];
  totalReviews: number;
  fetchMore: (page: number) => Promise<Review[]>;
}

// ============================================================================
// Styles (Aligning with your app's design system)
// ============================================================================
const gridCn = cntl`
  grid 
  grid-cols-1 
  md:grid-cols-4 
  gap-6 
  w-full 
  max-w-7xl 
  mx-auto 
  px-4 
  py-8
`;

const cardCn = cntl`
  flex 
  flex-col 
  bg-white 
  border 
  border-brand-truffle/10 
  rounded-2xl 
  p-5 
  shadow-sm 
  hover:shadow-md 
  transition-shadow 
  duration-300
`;

// ============================================================================
// Sub-component: Individual Review Card
// ============================================================================
const ReviewCard = ({ review, index }: { review: Review; index: number }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.05, ease: 'easeOut' }}
      className={cardCn}
    >
      {/* Stars */}
      <div className="flex items-center space-x-1 mb-3">
        {[...Array(5)].map((_, i) => (
          <Star
            key={i}
            size={16}
            className={
              i < review.rating
                ? 'fill-brand-gold text-brand-gold'
                : 'text-gray-200'
            }
          />
        ))}
      </div>

      {/* Title & Body */}
      {review.title && (
        <h4 className="font-display font-bold text-text-heading text-sm mb-2 line-clamp-1">
          {review.title}
        </h4>
      )}
      <p className="font-body text-text-muted text-sm leading-relaxed mb-4 flex-grow line-clamp-6">
        {review.body}
      </p>

      {/* Footer: Author & Meta */}
      <div className="mt-auto pt-4 border-t border-brand-truffle/10">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <span className="font-display font-[500] text-text-heading text-sm">
              {review.author}
            </span>
            {review.isVerified && (
              <span className="flex items-center text-[#17B26A] text-[10px] font-bold uppercase tracking-wider bg-[#17B26A]/10 px-1.5 py-0.5 rounded-full">
                <CheckCircle size={10} className="mr-1" /> Verified
              </span>
            )}
          </div>
          <span className="font-body text-xs text-gray-400">{review.date}</span>
        </div>
      </div>
    </motion.div>
  );
};

// ============================================================================
// Main Component
// ============================================================================
export const CustomerReviews = ({ product }: { product: Product }) => {
  const { data, count, isLoading, rating } = useReviews(product);

  return (
    <div className="w-full bg-brand-cream/30">
      <div className="max-w-7xl mx-auto px-4 py-0">
        <h2 className="font-display text-3xl font-bold text-text-heading text-center">
          Customer Reviews
        </h2>
        {/* NEW: The Auth-Gated Form injected right below the title */}
        {/* <WriteReviewForm product={product} /> */}
      </div>

      <InfiniteScroll
        dataLength={data.length}
        next={() => {}}
        hasMore={false}
        loader={
          <div className="flex justify-center items-center py-8 w-full col-span-full">
            <Loader2 className="animate-spin text-brand-truffle" size={32} />
          </div>
        }
        endMessage={
          <div className="text-center py-10 w-full col-span-full">
            <p className="font-body text-text-muted text-sm">
              {data.length === 0
                ? 'Be the first to review!'
                : "You've read all the reviews!"}
            </p>
          </div>
        }
        className={gridCn} // Applies the grid directly to the InfiniteScroll wrapper
      >
        {data.map((review, index) => (
          <ReviewCard key={review.id} review={review} index={index} />
        ))}
      </InfiniteScroll>
    </div>
  );
};

export default CustomerReviews;
